import * as React from "react"

/**
 * Subscribe to a CSS media query and re-render on every match change.
 *
 * SSR-safe: the server snapshot (and the first client render before
 * hydration) returns `defaultValue`, so layout-driving callers can pick the
 * shape that avoids the worst flash (e.g. assume desktop for a `min-width`
 * query so a wide table doesn't briefly collapse into cards on a monitor).
 *
 * @example
 * const isDesktop = useMediaQuery('(min-width: 768px)', { defaultValue: true })
 */
export function useMediaQuery(
  query: string,
  options?: { defaultValue?: boolean },
): boolean {
  const defaultValue = options?.defaultValue ?? false

  const subscribe = React.useCallback(
    (onStoreChange: () => void) => {
      if (typeof window === "undefined" || typeof window.matchMedia !== "function") {
        return () => {
          /* no matchMedia (SSR) — nothing to unsubscribe */
        }
      }
      const mql = window.matchMedia(query)
      mql.addEventListener("change", onStoreChange)
      return () => mql.removeEventListener("change", onStoreChange)
    },
    [query],
  )

  const getSnapshot = React.useCallback(() => {
    if (typeof window === "undefined" || typeof window.matchMedia !== "function") {
      return defaultValue
    }
    return window.matchMedia(query).matches
  }, [query, defaultValue])

  const getServerSnapshot = React.useCallback(() => defaultValue, [defaultValue])

  return React.useSyncExternalStore(subscribe, getSnapshot, getServerSnapshot)
}
